import React, { useContext, useState, useEffect, useMemo } from 'react';
import { LanguageCodes } from '../index';
import { getTranslation } from '../utils/getTranslation';
import { TranslationContext, DEFAULT_LANGUAGE } from './Translation.context';
import { parseSearchParams } from '../../helpers/parseSearchParams';
import {
  LanguageCode,
  TranslationContextType,
  TranslationObject,
  TranslationProviderProps,
} from '../types/translation.types';

export const TranslationProvider = ({ children }: TranslationProviderProps) => {
  const availableLanguages = Object.keys(LanguageCodes) as LanguageCode[];
  const isWindowDefined = typeof window !== 'undefined';

  const getLanguageFromURL = (): LanguageCode => {
    if (isWindowDefined) {
      const langParam = parseSearchParams('lang') as LanguageCode;
      return langParam && availableLanguages.includes(langParam)
        ? langParam
        : DEFAULT_LANGUAGE;
    }
    return DEFAULT_LANGUAGE;
  };

  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    getLanguageFromURL()
  );

  const [currentTranslations, setCurrentTranslations] =
    useState<TranslationObject>(
      LanguageCodes[currentLanguage] || LanguageCodes[DEFAULT_LANGUAGE] || {}
    );

  const t = useMemo(() => {
    return getTranslation(currentTranslations, currentLanguage);
  }, [currentTranslations, currentLanguage]);

  useEffect(() => {
    if (!isWindowDefined) return;

    const urlLang = parseSearchParams('lang') as LanguageCode;
    const validLang =
      urlLang && availableLanguages.includes(urlLang)
        ? urlLang
        : DEFAULT_LANGUAGE;
    if (validLang !== currentLanguage) {
      setCurrentLanguage(validLang);
      setCurrentTranslations(
        LanguageCodes[validLang] || LanguageCodes[DEFAULT_LANGUAGE] || {}
      );
    }
  }, [currentLanguage]);

  useEffect(() => {
    const newTranslations = LanguageCodes[currentLanguage];
    if (newTranslations) {
      setCurrentTranslations(newTranslations);
    }
  }, [currentLanguage]);

  const contextValue: TranslationContextType = {
    currentLanguage,
    t,
    availableLanguages,
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
